import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiClient } from '../config/api';

/**
 * 90-day post-publication shape, small enough for a table row.
 *
 * Fetched for the WHOLE page in one request and shared through context. A
 * per-row fetch would make the list N+1 — which is the usual reason sparklines
 * get designed and then quietly dropped when the table gets long.
 *
 * No axes, no labels: the job is not to be read precisely, it is to make a
 * scrolling reader stop at the one that fell off a cliff.
 */

const SparklineContext = createContext({ byId: new Map(), loading: true });

export const SparklineProvider = ({ children, reportIds = [], days = 90 }) => {
  const [value, setValue] = useState({ byId: new Map(), loading: true });
  // Join once so the effect re-runs on a page change but not on every render
  // that happens to rebuild the array.
  const idKey = reportIds.join(',');

  useEffect(() => {
    let cancelled = false;
    if (!idKey) { setValue({ byId: new Map(), loading: false }); return; }
    apiClient
      .get('/api/price-impact/sparklines', {
        params: { ids: idKey, days, limit: Math.min(reportIds.length, 200) },
      })
      .then((res) => {
        if (cancelled) return;
        const byId = new Map(res.data.map((r) => [r.report_id, r.points]));
        setValue({ byId, loading: false });
      })
      .catch(() => {
        // Sparklines are decoration on a table that works without them. A
        // failure here must not take the list down with it.
        if (!cancelled) setValue({ byId: new Map(), loading: false });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey, days]);

  return <SparklineContext.Provider value={value}>{children}</SparklineContext.Provider>;
};

const PriceSparkline = ({ reportId, width = 96, height = 26 }) => {
  const { byId, loading } = useContext(SparklineContext);
  const pts = byId.get(reportId);

  if (loading) return <div className="bg-gray-100 rounded animate-pulse" style={{ width, height }} />;
  if (!pts || pts.length < 4) return <span className="text-gray-300 text-xs">—</span>;

  const lo = Math.min(...pts), hi = Math.max(...pts);
  const span = hi - lo || 1;
  const x = (i) => (i * width) / (pts.length - 1);
  const y = (v) => 2 + ((hi - v) * (height - 4)) / span;
  const d = 'M' + pts.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('L');

  const last = pts[pts.length - 1];
  const delta = last - 100;
  // Down is the short working, so green means the thesis paid.
  const stroke = delta < 0 ? '#1a7f4b' : '#c0392b';

  return (
    <div className="flex items-center gap-2">
      <svg width={width} height={height} role="img"
           aria-label={`90 days after publication, ending ${delta >= 0 ? 'up' : 'down'} ${Math.abs(delta).toFixed(0)} percent`}>
        {/* the 100 line: above it the short is underwater */}
        <line x1="0" x2={width} y1={y(100)} y2={y(100)}
              stroke="#d5d4cf" strokeWidth="1" strokeDasharray="2 2" />
        <path d={d} fill="none" stroke={stroke} strokeWidth="1.5"
              strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={x(pts.length - 1)} cy={y(last)} r="2.2" fill={stroke} />
      </svg>
      <span className="font-mono text-xs tabular-nums"
            style={{ color: stroke }}>
        {delta >= 0 ? '+' : ''}{delta.toFixed(0)}%
      </span>
    </div>
  );
};

export default PriceSparkline;
